import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, HelpCircle, Shield, Info, RefreshCw, Volume2, CheckCircle2, ChevronDown, Check, AlertCircle, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../supabaseClient";

const ModernDropdown = ({ label, options, value, onChange, placeholder, isDark, submitted }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef} onKeyDown={handleKeyDown}>
      {label && (
        <label className={`text-[12px] font-medium px-4 mb-2 block ${isDark ? 'text-gray-500' : 'text-[#065DC0] font-bold uppercase tracking-wider'}`}>
          {label}
        </label>
      )}
      <button
        type="button"
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full ${isDark ? (submitted && !value ? 'bg-red-500/10 border-red-500/50 text-white' : 'bg-[#065DC0]/5 border-white/10 text-white') : (submitted && !value ? 'bg-red-500/5 border-red-500/50 text-black' : 'bg-[#065DC0]/[0.02] border-gray-200 text-black')} border rounded-full px-5 py-3 text-[15px] focus:outline-none transition-all flex items-center justify-between text-left focus:ring-2 focus:ring-[#065DC0]/50`}
      >
        <span className={`inline-flex items-center gap-2 ${!value ? (isDark ? 'text-gray-500' : 'text-gray-400') : ''}`}>
          {typeof value === 'string' && value.includes(' ') ? value : options.find(o => (typeof o === 'object' ? o.value : o) === value)?.label || value || placeholder}
        </span>
        <ChevronDown size={16} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''} opacity-50`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 5, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className={`absolute z-[100] w-full mt-1 ${isDark ? 'bg-[#1c1c1e] border-white/10' : 'bg-white border-gray-200'} border rounded-[32px] shadow-2xl overflow-hidden max-h-[300px] overflow-y-auto backdrop-blur-xl`}
          >
            <div className="py-1">
              {options.map((option) => (
                <button
                  key={typeof option === 'object' ? option.value : option}
                  type="button"
                  onClick={() => {
                    onChange(typeof option === 'object' ? option.value : option);
                    setIsOpen(false);
                  }}
                  className={`w-full px-5 py-3 text-[15px] text-left flex items-center justify-between transition-colors ${
                    (typeof option === 'object' ? option.value === value : option === value)
                      ? (isDark ? 'bg-[#065DC0]/20 text-[#065DC0]' : 'bg-[#065DC0]/10 text-[#065DC0]')
                      : (isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-700')
                  }`}
                >
                  <span className="inline-flex items-center gap-2">{typeof option === 'object' ? option.label : option}</span>
                  {(typeof option === 'object' ? option.value === value : option === value) && (
                    <Check size={16} />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Static constants moved outside component for performance
const countries = [
  { name: "India", code: "+91", flag: "🇮🇳" },
  { name: "United States", code: "+1", flag: "🇺🇸" },
  { name: "United Kingdom", code: "+44", flag: "🇬🇧" },
  { name: "Australia", code: "+61", flag: "🇦🇺" },
  { name: "Canada", code: "+1", flag: "🇨🇦" },
  { name: "Germany", code: "+49", flag: "🇩🇪" },
  { name: "France", code: "+33", flag: "🇫🇷" },
  { name: "Japan", code: "+81", flag: "🇯🇵" },
  { name: "China", code: "+86", flag: "🇨🇳" },
  { name: "Brazil", code: "+55", flag: "🇧🇷" },
  { name: "South Africa", code: "+27", flag: "🇿🇦" },
  { name: "United Arab Emirates", code: "+971", flag: "🇦🇪" },
  { name: "Singapore", code: "+65", flag: "🇸🇬" },
  { name: "Russia", code: "+7", flag: "🇷🇺" },
  { name: "Mexico", code: "+52", flag: "🇲🇽" },
  { name: "Italy", code: "+39", flag: "🇮🇹" },
  { name: "Spain", code: "+34", flag: "🇪🇸" },
  { name: "Netherlands", code: "+31", flag: "🇳🇱" },
  { name: "Sweden", code: "+46", flag: "🇸🇪" },
  { name: "Norway", code: "+47", flag: "🇳🇴" },
  { name: "Denmark", code: "+45", flag: "🇩🇰" },
  { name: "Finland", code: "+358", flag: "🇫🇮" },
  { name: "New Zealand", code: "+64", flag: "🇳🇿" },
  { name: "Ireland", code: "+353", flag: "🇮🇪" },
  { name: "Switzerland", code: "+41", flag: "🇨🇭" },
  { name: "Austria", code: "+43", flag: "🇦🇹" },
  { name: "Belgium", code: "+32", flag: "🇧🇪" },
  { name: "Israel", code: "+972", flag: "🇮🇱" },
  { name: "South Korea", code: "+82", flag: "🇰🇷" },
  { name: "Turkey", code: "+90", flag: "🇹🇷" },
  { name: "Saudi Arabia", code: "+966", flag: "🇸🇦" },
  { name: "Argentina", code: "+54", flag: "🇦🇷" },
  { name: "Chile", code: "+56", flag: "🇨🇱" },
  { name: "Colombia", code: "+57", flag: "🇨🇴" },
  { name: "Peru", code: "+51", flag: "🇵🇪" },
  { name: "Egypt", code: "+20", flag: "🇪🇬" },
  { name: "Thailand", code: "+66", flag: "🇹🇭" },
  { name: "Vietnam", code: "+84", flag: "🇻🇳" },
  { name: "Malaysia", code: "+60", flag: "🇲🇾" },
  { name: "Indonesia", code: "+62", flag: "🇮🇩" },
  { name: "Philippines", code: "+63", flag: "🇵🇭" },
  { name: "Pakistan", code: "+92", flag: "🇵🇰" },
  { name: "Bangladesh", code: "+880", flag: "🇧🇩" },
  { name: "Nigeria", code: "+234", flag: "🇳🇬" },
  { name: "Kenya", code: "+254", flag: "🇰🇪" },
  { name: "Morocco", code: "+212", flag: "🇲🇦" },
  { name: "Portugal", code: "+351", flag: "🇵🇹" },
  { name: "Greece", code: "+30", flag: "🇬🇷" },
  { name: "Poland", code: "+48", flag: "🇵🇱" },
  { name: "Ukraine", code: "+380", flag: "🇺🇦" }
].sort((a, b) => a.name.localeCompare(b.name));

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const days = Array.from({ length: 31 }, (_, i) => (i + 1).toString());
const years = Array.from({ length: 121 }, (_, i) => (new Date().getFullYear() - i).toString());

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get("redirect");
  const formRef = useRef(null);
  const [theme, setTheme] = useState(() => 
    window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  );
  const { signup, isAuthenticated, isLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [showDobTooltip, setShowDobTooltip] = useState(false);
  
  const [captchaText, setCaptchaText] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [passStrength, setPassStrength] = useState({ score: 0, label: "Weak", color: "bg-red-500" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    countryRegion: "India",
    dobDay: "",
    dobMonth: "",
    dobYear: "",
    email: "",
    otp: "",
    password: "",
    confirmPassword: "",
    phone: "",
    phonePrefix: "+91",
    address: "",
    state: "",
    pincode: "",
    country: "India"
  });

  const [generatedOtp, setGeneratedOtp] = useState("");
  const [resending, setResending] = useState(false);

  const generateCaptcha = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let text = "";
    for (let i = 0; i < 6; i++) {
      text += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(text);
    setCaptchaInput("");
  };

  const playCaptchaAudio = () => {
    const speak = (text) => {
      const utterance = new SpeechSynthesisUtterance(text.split('').join(' '));
      utterance.rate = 0.8;
      window.speechSynthesis.speak(utterance);
    };
    speak(`Your verification code is ${captchaText}`);
  };

  const calculateStrength = (pass) => {
    let score = 0;
    if (pass.length === 0) return { score: 0, label: "Weak", color: "bg-red-500", text: "text-red-500" };
    if (pass.length >= 8) score += 1;
    if (/[A-Z]/.test(pass)) score += 1;
    if (/[a-z]/.test(pass)) score += 1;
    if (/[0-9]/.test(pass)) score += 1;
    if (/[^A-Za-z0-9]/.test(pass)) score += 1;

    if (score < 3) return { score, label: "Low", color: "bg-red-500", text: "text-red-500" };
    if (score < 5) return { score, label: "Medium", color: "bg-yellow-500", text: "text-yellow-500" };
    return { score, label: "High", color: "bg-green-500", text: "text-green-500" };
  };

  useEffect(() => {
    // Only redirect if authenticated AND not currently in the success flow
    if (isAuthenticated && !isLoading && !showSuccess) {
      navigate('/account', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, showSuccess]);

  useEffect(() => {
    let mounted = true;
    const detectCountry = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout

        const response = await fetch('https://get.geojs.io/v1/ip/geo.json', { signal: controller.signal });
        clearTimeout(timeoutId);
        
        const data = await response.json();
        if (mounted && data.country) {
          setFormData(prev => ({
            ...prev,
            countryRegion: data.country,
            country: data.country,
            // geojs doesn't provide country calling code, so we leave phonePrefix unchanged or use a lookup if needed,
            // but for now we'll just set the country and let the user pick the prefix if it's currently default.
          }));
        }
      } catch (err) {
        // Silent catch to keep console clean
      }
    };
    detectCountry();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (formData.dobDay && formData.dobMonth && formData.dobYear) {
      const birthDate = new Date(
        parseInt(formData.dobYear),
        months.indexOf(formData.dobMonth),
        parseInt(formData.dobDay)
      );
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      if (age < 12) {
        setError("You must be at least 12 years old to create a FOZZIL ID.");
      } else if (error === "You must be at least 12 years old to create a FOZZIL ID.") {
        setError("");
      }
    }
  }, [formData.dobDay, formData.dobMonth, formData.dobYear]);

  useEffect(() => {
    generateCaptcha();
  }, []);

  useEffect(() => {
    setPassStrength(calculateStrength(formData.password));
  }, [formData.password]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleThemeChange = (e) => setTheme(e.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", handleThemeChange);
    return () => mediaQuery.removeEventListener("change", handleThemeChange);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-white/20 border-t-[#065DC0] rounded-full animate-spin"></div>
      </div>
    );
  }

  const handleGlobalKeyDown = (e) => {
    if (e.key === "Enter") {
      const target = e.target;
      if (target.tagName === "INPUT" || target.tagName === "SELECT" || target.tagName === "BUTTON") {
        if (target.type === "submit") return;
        
        e.preventDefault();
        const form = formRef.current;
        if (!form) return;

        const focusableElements = Array.from(form.querySelectorAll('input, select, button, [tabindex="0"]'));
        const index = focusableElements.indexOf(target);
        
        if (index > -1 && index < focusableElements.length - 1) {
          const nextElement = focusableElements[index + 1];
          if (nextElement) nextElement.focus();
        }
      }
    }
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const verifyEmail = async () => {
    if (!formData.email || !formData.email.includes('@')) {
      setError("Please enter a valid email address");
      return;
    }
    
    setEmailVerifying(true);
    setError("");

    // Generate a secure 6-digit OTP
    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(otp);

    try {
      const backendUrl = (process.env.REACT_APP_API_URL || process.env.REACT_APP_FPAY_API_URL || 'https://fpaybeshadesecure.fozzilindustries.workers.dev').replace(/\/+$/, '');
      const response = await fetch(`${backendUrl}/api/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email, otp }),
      });

      if (!response.ok) {
        throw new Error("Failed to send verification email. Please check your connection.");
      }

      setOtpSent(true);
    } catch (err) {
      console.error("Email verification error:", err);
      setError(err.message || "An error occurred while sending the verification code.");
    } finally {
      setEmailVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (resending) return;
    setResending(true);
    await verifyEmail();
    setResending(false);
  };

  const checkOtp = (val) => {
    const codeToVerify = typeof val === 'string' ? val : formData.otp;
    if (codeToVerify === generatedOtp) {
      setEmailVerified(true);
      setOtpSent(false);
      setError("");
    } else if (codeToVerify.length === 6) {
      setError("Invalid verification code. Please check your email.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    const firstInvalid = formRef.current?.querySelector(':invalid');
    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInvalid.focus();
      return;
    }

    // Manual check for custom required fields
    if (!formData.dobDay || !formData.dobMonth || !formData.dobYear || formData.dobDay === "Day" || formData.dobMonth === "Month" || formData.dobYear === "Year") {
      setError("Please complete your Date of Birth.");
      document.getElementById('dob-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!emailVerified) {
      setError("Please verify your email address first.");
      document.getElementsByName('email')[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    if (!formData.phone) {
      setError("Please enter your phone number.");
      document.getElementsByName('phone')[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const fullPhone = `${formData.phonePrefix}${formData.phone}`;
      const metadata = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: fullPhone,
        country: formData.countryRegion,
        address: formData.address,
        state: formData.state,
        pincode: formData.pincode,
        dob_day: formData.dobDay,
        dob_month: formData.dobMonth,
        dob_year: formData.dobYear
      };

      // 1. Check if user already exists
      const { data: existing, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .or(`email.eq.${formData.email},phone.eq.${fullPhone}`)
        .maybeSingle();

      if (existing) {
        setError("An account with this email or phone number already exists.");
        setLoading(false);
        return;
      }

      // 3. Final validation checks
      const birthDate = new Date(
        parseInt(formData.dobYear),
        months.indexOf(formData.dobMonth),
        parseInt(formData.dobDay)
      );
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      if (age < 12) {
        setError("You must be at least 12 years old to create a FOZZIL ID.");
        setLoading(false);
        document.getElementById('dob-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (captchaInput.toUpperCase() !== captchaText) {
        setError("Invalid captcha characters.");
        generateCaptcha();
        setLoading(false);
        document.getElementById('security-check-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (passStrength.label === "Low") {
        setError("Please use a stronger password.");
        setLoading(false);
        document.getElementsByName('password')[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // 4. Perform Supabase Auth Signup
      console.log("Attempting signup for:", formData.email);
      const result = await signup(formData.email, formData.password, metadata);
      const authData = result?.data;
      const authError = result?.error;
      
      if (authError) {
        console.error("Supabase Auth Error:", authError);
        if (authError.message.includes("already registered") || authError.message.includes("already exists")) {
          setError("An account with this email address already exists.");
        } else {
          setError(`Auth Error: ${authError.message}`);
        }
        setLoading(false);
        return;
      }

      if (authData?.user) {
        // Profile is handled automatically by the Supabase SQL Trigger
        console.log("Signup success: User created with metadata.");

        // 6. Send Welcome Email via Backend
        try {
          const backendUrl = (process.env.REACT_APP_API_URL || process.env.REACT_APP_FPAY_API_URL || 'https://fpaybeshadesecure.fozzilindustries.workers.dev').replace(/\/+$/, '');
          await fetch(`${backendUrl}/api/send-email`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              subject: "Your FOZZIL ID Created Successfully",
              data: {
                message: "Your FOZZIL ID is now active.",
                accountID: formData.email,
                creationDate: new Date().toLocaleDateString()
              }
            }),
          });
        } catch (e) {
          console.error("Welcome email failed, but account is created.");
        }

        // 7. Clear the auto-login session to force manual login
        await supabase.auth.signOut();

        setShowSuccess(true);
        setTimeout(() => {
          const authUrl = redirectPath 
            ? `/auth?redirect=${encodeURIComponent(redirectPath)}` 
            : '/auth';
          navigate(authUrl, { state: { signupSuccess: true } });
        }, 4000);
      }
    } catch (err) {
      setError(err.message || "An error occurred during signup");
    } finally {
      setLoading(false);
    }
  };


  const isDark = theme === "dark";

  return (
    <div className={`min-h-screen ${isDark ? 'bg-black text-white' : 'bg-[#F5F5F7] text-black'} flex flex-col items-center py-12 px-6 relative overflow-x-hidden font-sans transition-colors duration-500`}>
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute top-0 right-1/4 w-[500px] h-[500px] ${isDark ? 'bg-[#065DC0]/[0.03]' : 'bg-[#065DC0]/[0.01]'} blur-[150px] rounded-full`}></div>
        <div className={`absolute bottom-0 left-1/4 w-[500px] h-[500px] ${isDark ? 'bg-[#065DC0]/[0.03]' : 'bg-[#065DC0]/[0.01]'} blur-[150px] rounded-full`}></div>
      </div>

      <div className="w-full max-w-[500px] relative z-10">
        <div className="text-center mb-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`overflow-hidden rounded-[2.8rem] ${isDark ? 'bg-gradient-to-b from-white/10 to-transparent border-white/5 shadow-2xl' : 'bg-gradient-to-b from-black/[0.05] to-transparent border-black/5 shadow-xl'} border mb-8 flex items-center justify-center w-28 h-28 mx-auto`}
          >
            <img src="/logoicon.png" alt="FOZZIL Icon" className="w-full h-full object-cover" />
          </motion.div>
          <h1 className="text-[32px] font-normal tracking-tight mb-2">Create Your FOZZIL ID</h1>
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-[16px] mb-2`}>One FOZZIL ID is all you need access all FOZZIL services.</p>
          <p className="text-[14px]">Already have a FOZZIL ID? <Link to={redirectPath ? `/auth?redirect=${encodeURIComponent(redirectPath)}` : '/auth'} className="text-[#065DC0] hover:underline">Sign In</Link></p>
        </div>

        <form onSubmit={handleSubmit} ref={formRef} onKeyDown={handleGlobalKeyDown} className="space-y-8">
          <div className="space-y-4">
            <label className={`text-[12px] font-medium px-4 ${isDark ? 'text-gray-500' : 'text-[#065DC0] font-bold uppercase tracking-wider'}`}>PERSONAL INFORMATION</label>
            <div className="grid grid-cols-2 gap-3">
              <input required name="firstName" placeholder="First name" value={formData.firstName} onChange={handleChange} className={`w-full ${isDark ? (submitted && !formData.firstName ? 'bg-red-500/10 border-red-500/50 text-white' : 'bg-[#065DC0]/5 border-white/10 text-white') : (submitted && !formData.firstName ? 'bg-red-500/5 border-red-500/50' : 'bg-[#065DC0]/[0.02] border-gray-200')} border rounded-full px-5 py-3 text-[15px] focus:outline-none transition-all placeholder-gray-400`} />
              <input required name="lastName" placeholder="Last name" value={formData.lastName} onChange={handleChange} className={`w-full ${isDark ? (submitted && !formData.lastName ? 'bg-red-500/10 border-red-500/50 text-white' : 'bg-[#065DC0]/5 border-white/10 text-white') : (submitted && !formData.lastName ? 'bg-red-500/5 border-red-500/50' : 'bg-[#065DC0]/[0.02] border-gray-200')} border rounded-full px-5 py-3 text-[15px] focus:outline-none transition-all placeholder-gray-400`} />
            </div>
          </div>

          <hr className={isDark ? 'border-white/10' : 'border-black/10'} />

          <div className="space-y-5">
            <ModernDropdown label="COUNTRY / REGION" options={countries.map(c => ({ label: `${c.flag} ${c.name}`, value: c.name }))} value={formData.countryRegion} submitted={submitted} onChange={(val) => {
              const country = countries.find(c => c.name === val);
              setFormData(prev => ({ ...prev, countryRegion: val, phonePrefix: country?.code || prev.phonePrefix, country: country?.name || prev.country }));
            }} isDark={isDark} />

            <div className="space-y-2" id="dob-section">
              <div className="flex items-center gap-2 px-4 relative">
                <label className={`text-[12px] font-medium ${isDark ? 'text-gray-500' : 'text-[#065DC0] font-bold uppercase tracking-wider'}`}>DATE OF BIRTH</label>
                <div className="relative cursor-help" onMouseEnter={() => setShowDobTooltip(true)} onMouseLeave={() => setShowDobTooltip(false)}>
                  <HelpCircle size={14} className="text-[#065DC0]" />
                  <AnimatePresence>
                    {showDobTooltip && (
                      <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 5 }} className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-black text-white text-[11px] rounded-lg shadow-xl z-50 text-center pointer-events-none border border-white/10">
                        This information is used to determine the services available for your account.
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-black"></div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <ModernDropdown options={days} value={formData.dobDay} placeholder="Day" onChange={(val) => setFormData(prev => ({ ...prev, dobDay: val }))} isDark={isDark} submitted={submitted} />
                <ModernDropdown options={months} value={formData.dobMonth} placeholder="Month" onChange={(val) => setFormData(prev => ({ ...prev, dobMonth: val }))} isDark={isDark} submitted={submitted} />
                <ModernDropdown options={years} value={formData.dobYear} placeholder="Year" onChange={(val) => setFormData(prev => ({ ...prev, dobYear: val }))} isDark={isDark} submitted={submitted} />
              </div>
              {error === "You must be at least 12 years old to create a FOZZIL ID." && (
                <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="text-red-500 text-[12px] px-4 font-medium flex items-center gap-1.5 mt-1.5 transition-all">
                  You must be at least 12 years old to create a FOZZIL ID.
                </motion.p>
              )}
            </div>
          </div>

          <hr className={isDark ? 'border-white/10' : 'border-black/10'} />

          <div className="space-y-4">
            <label className={`text-[12px] font-medium px-4 ${isDark ? 'text-gray-500' : 'text-[#065DC0] font-bold uppercase tracking-wider'}`}>ACCOUNT SECURITY</label>
            <div className="space-y-3">
              <div className="relative">
                <input required type="email" name="email" placeholder="name@example.com" value={formData.email} onChange={handleChange} disabled={emailVerified} className={`w-full ${isDark ? (submitted && !formData.email ? 'bg-red-500/10 border-red-500/50 text-white' : 'bg-[#065DC0]/5 border-white/10 text-white') : (submitted && !formData.email ? 'bg-red-500/5 border-red-500/50' : 'bg-[#065DC0]/[0.02] border-gray-200')} border rounded-full px-5 py-3 text-[15px] focus:outline-none transition-all placeholder-gray-400 pr-24`} />
                {!emailVerified && (
                  <button
                    type="button"
                    onClick={verifyEmail}
                    disabled={emailVerifying || !formData.email}
                    className="absolute right-5 top-1/2 -translate-y-1/2 text-[#065DC0] hover:text-[#065DC0]/80 text-[14px] font-medium disabled:opacity-50 transition-all"
                  >
                    {emailVerifying ? <RefreshCw size={14} className="animate-spin" /> : "Verify"}
                  </button>
                )}
                {emailVerified && (
                  <div className="absolute right-5 top-1/2 -translate-y-1/2 text-green-600 flex items-center gap-1.5 text-[14px] font-semibold">
                    <CheckCircle2 size={16} /> Verified
                  </div>
                )}
              </div>
              <AnimatePresence>
                {otpSent && !emailVerified && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    exit={{ opacity: 0, y: -10 }} 
                    className="space-y-4 pt-1 w-full md:w-1/2 mx-auto"
                  >
                    <div className="relative">
                      <input 
                        type="text" 
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name="otp" 
                        placeholder="&bull; &bull; &bull; &bull; &bull; &bull;" 
                        value={formData.otp} 
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                          setFormData(prev => ({ ...prev, otp: val }));
                          if (val.length === 6) {
                            checkOtp(val);
                          }
                        }} 
                        className={`w-full ${isDark ? 'bg-[#065DC0]/5 border-white/10 text-white' : 'bg-[#065DC0]/[0.02] border-gray-200'} border rounded-full px-8 py-3 text-[18px] tracking-[0.5em] text-center focus:outline-none placeholder-gray-400 font-mono`} 
                      />
                    </div>
                    <div className="flex items-center justify-between px-4">
                       <button 
                        type="button" 
                        onClick={handleResendOtp}
                        disabled={resending}
                        className="text-[#065DC0] text-[13px] font-medium hover:underline disabled:opacity-50"
                      >
                        {resending ? "Sending..." : "Resend OTP"}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              {submitted && !emailVerified && !otpSent && (
                <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="text-red-500 text-[12px] px-4 font-medium flex items-center gap-1.5 mt-1 transition-all">
                  Please verify your email address.
                </motion.p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="relative">
                <input 
                  required 
                  type={showPassword ? "text" : "password"} 
                  name="password" 
                  placeholder="Password" 
                  value={formData.password} 
                  onChange={handleChange} 
                  className={`w-full ${isDark ? (submitted && !formData.password ? 'bg-red-500/10 border-red-500/50 text-white' : 'bg-[#065DC0]/5 border-white/10 text-white') : (submitted && !formData.password ? 'bg-red-500/5 border-red-500/50' : 'bg-[#065DC0]/[0.02] border-gray-200')} border rounded-full px-5 py-3 text-[15px] focus:outline-none transition-all placeholder-gray-400 pr-12`} 
                />
                <button
                  type="button"
                  tabIndex="-1"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors flex items-center justify-center p-1 rounded-full hover:bg-white/5`}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              

              <div className="relative">
                <input 
                  required 
                  type={showConfirmPassword ? "text" : "password"} 
                  name="confirmPassword" 
                  placeholder="Confirm Password" 
                  value={formData.confirmPassword} 
                  onChange={handleChange} 
                  className={`w-full ${isDark ? (submitted && !formData.confirmPassword ? 'bg-red-500/10 border-red-500/50 text-white' : 'bg-[#065DC0]/5 border-white/10 text-white') : (submitted && !formData.confirmPassword ? 'bg-red-500/5 border-red-500/50' : 'bg-[#065DC0]/[0.02] border-gray-200')} border rounded-full px-5 py-3 text-[15px] focus:outline-none transition-all placeholder-gray-400 pr-12`} 
                />
                <button
                  type="button"
                  tabIndex="-1"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors flex items-center justify-center p-1 rounded-full hover:bg-white/5`}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {submitted && formData.password !== formData.confirmPassword && (
                <div className="col-span-2">
                  <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="text-red-500 text-[12px] px-4 font-medium flex items-center gap-1.5 mt-1 transition-all">
                    Passwords do not match.
                  </motion.p>
                </div>
              )}

              {formData.password && (
                <div className="px-4 space-y-2 col-span-2">
                  <div className="flex justify-between items-center">
                    <span className={`text-[12px] font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Password Intensity:</span>
                    <span className={`text-[12px] font-bold ${passStrength.text}`}>{passStrength.label}</span>
                  </div>
                  <div className="flex gap-1.5 h-1">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className={`flex-1 rounded-full transition-all duration-500 ${i <= (passStrength.score >= 5 ? 3 : passStrength.score >= 3 ? 2 : 1) ? passStrength.color : (isDark ? 'bg-white/10' : 'bg-gray-100')}`}></div>
                    ))}
                  </div>
                </div>
              )}

              {submitted && formData.password && passStrength.label === "Low" && (
                <div className="col-span-2">
                  <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="text-red-500 text-[12px] px-4 font-medium flex items-center gap-1.5 mt-1 transition-all">
                    Password is too weak. Please use a stronger password.
                  </motion.p>
                </div>
              )}
            </div>
          </div>

          <hr className={isDark ? 'border-white/10' : 'border-black/10'} />

          <div className="space-y-4">
            <label className={`text-[12px] font-medium px-4 ${isDark ? 'text-gray-500' : 'text-[#065DC0] font-bold uppercase tracking-wider'}`}>CONTACT INFORMATION</label>
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-12 md:col-span-5">
                <ModernDropdown 
                  options={countries.map(c => ({ label: `${c.flag} ${c.code} ${c.name}`, value: c.code }))} 
                  value={formData.phonePrefix} 
                  onChange={(val) => setFormData(prev => ({ ...prev, phonePrefix: val }))} 
                  isDark={isDark} 
                  submitted={submitted}
                />
              </div>
              <input required type="tel" name="phone" placeholder="Phone number" value={formData.phone} onChange={handleChange} className={`col-span-12 md:col-span-7 ${isDark ? (submitted && !formData.phone ? 'bg-red-500/10 border-red-500/50 text-white' : 'bg-[#065DC0]/5 border-white/10 text-white') : (submitted && !formData.phone ? 'bg-red-500/5 border-red-500/50' : 'bg-[#065DC0]/[0.02] border-gray-200')} border rounded-full px-5 py-3 text-[15px] focus:outline-none transition-all placeholder-gray-400`} />
            </div>
          </div>


          <hr className={isDark ? 'border-white/10' : 'border-black/10'} />

          <div className="space-y-6" id="security-check-section">
            <div className="space-y-3 px-4">
              <label className={`text-[12px] font-medium ${isDark ? 'text-gray-500' : 'text-[#065DC0] font-bold uppercase tracking-wider'}`}>SECURITY CHECK</label>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col md:flex-row items-center gap-4">
                    <div 
                      className="w-48 h-12 bg-white rounded-lg flex items-center justify-center relative overflow-hidden flex-shrink-0 border border-gray-300 shadow-inner"
                      style={{
                        backgroundImage: `
                          repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.05) 3px, rgba(0,0,0,0.05) 4px),
                          repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(0,0,0,0.05) 3px, rgba(0,0,0,0.05) 4px)
                        `
                      }}
                    >
                      <div className="flex items-center justify-center gap-[2px]">
                        {captchaText.split('').map((char, i) => (
                          <span 
                            key={i} 
                            className="text-2xl font-black text-black select-none inline-block"
                            style={{
                              transform: `rotate(${Math.floor(Math.random() * 40) - 20}deg) translateY(${Math.floor(Math.random() * 6) - 3}px)`,
                              filter: 'blur(0.2px)',
                              letterSpacing: '-2px'
                            }}
                          >
                            {char}
                          </span>
                        ))}
                      </div>
                      <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute w-full h-[1px] bg-black/10 top-1/4 -rotate-2"></div>
                        <div className="absolute w-full h-[1px] bg-black/10 top-3/4 rotate-2"></div>
                      </div>
                    </div>

                    <input 
                      type="text" 
                      placeholder="Type the characters in the image" 
                      value={captchaInput} 
                      onChange={(e) => setCaptchaInput(e.target.value)} 
                      className={`flex-1 ${isDark ? (submitted && !captchaInput ? 'bg-red-500/10 border-red-500/50 text-white' : 'bg-[#065DC0]/5 border-white/10 text-white') : (submitted && !captchaInput ? 'bg-red-500/5 border-red-500/50' : 'bg-[#065DC0]/[0.02] border-gray-200')} border rounded-full px-5 py-3 text-[15px] focus:outline-none transition-all placeholder-gray-400`} 
                    />
                  </div>

                  <div className="flex items-center gap-3 px-2">
                    <button 
                      type="button" 
                      onClick={generateCaptcha} 
                      className="text-[#065DC0] flex items-center gap-1.5 text-[14px] font-medium hover:underline transition-all"
                    >
                      <RefreshCw size={14} /> New code
                    </button>
                    <span className={isDark ? 'text-gray-800' : 'text-gray-200'}>|</span>
                    <button 
                      type="button" 
                      onClick={playCaptchaAudio} 
                      className="text-[#065DC0] flex items-center gap-1.5 text-[14px] font-medium hover:underline transition-all"
                    >
                      <Volume2 size={14} /> Visually-impaired
                    </button>
                  </div>

                  {submitted && captchaInput.toUpperCase() !== captchaText && (
                    <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="text-red-500 text-[12px] px-2 font-medium flex items-center gap-1.5 transition-all">
                      Security check failed. Please try again.
                    </motion.p>
                  )}
                </div>
            </div>
          </div>


          <div className="flex flex-row items-center justify-center gap-4 pt-4 pb-12">
            <button onClick={() => navigate('/auth')} type="button" className={`w-40 py-3 rounded-full ${isDark ? 'text-gray-400 hover:bg-white/5' : 'text-gray-500 hover:bg-gray-100'} transition-all font-medium text-[15px]`}>Cancel</button>
            <button type="submit" disabled={loading} className={`w-40 bg-[#065DC0] hover:bg-[#073d82] text-white py-3.5 rounded-full font-semibold text-[16px] transition-all shadow-xl shadow-[#065DC0]/20 flex items-center justify-center gap-2 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}>
              {loading ? <RefreshCw size={18} className="animate-spin" /> : 'Continue'}
            </button>
          </div>
          {error && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-red-500 text-[13px] text-center font-medium mt-4">
              {error}
            </motion.p>
          )}
        </form>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-16 text-center">
          <div className="flex items-center justify-center gap-6 mb-12">
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className={`text-[13px] ${isDark ? 'text-gray-500 hover:text-[#065DC0]' : 'text-gray-400 hover:text-[#065DC0]'} transition-colors`}>Privacy Policy</a>
            <span className={isDark ? 'text-gray-800' : 'text-gray-200'}>|</span>
            <a href="/terms" target="_blank" rel="noopener noreferrer" className={`text-[13px] ${isDark ? 'text-gray-500 hover:text-[#065DC0]' : 'text-gray-400 hover:text-[#065DC0]'} transition-colors`}>Terms of Use</a>
          </div>
        </motion.div>
      </div>
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className={`p-10 rounded-[2.5rem] ${isDark ? 'bg-zinc-900 border-white/10' : 'bg-white border-black/5'} border shadow-2xl text-center max-w-sm mx-4`}
            >
              <div className="w-20 h-20 bg-[#065DC0]/10 rounded-full flex items-center justify-center mx-auto mb-6 overflow-hidden">
                <img src="/logoicon.png" alt="FOZZIL" className="w-12 h-12 object-contain select-none" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-[#065DC0]">Account Active!</h2>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                Your FOZZIL ID is ready to use. Redirecting to login...
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className={`fixed bottom-0 left-0 w-full h-[1px] ${isDark ? 'bg-gradient-to-r from-transparent via-white/10 to-transparent' : 'bg-gradient-to-r from-transparent via-black/5 to-transparent'}`}></div>
    </div>
  );
};

export default Signup;
